import React, { useState, useEffect, useContext } from 'react';
import { LogOut, ChevronLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { getDisplayName } from '../../utils/displayName';
import { propertiesAPI } from '../../services/api';
import { paymentStatusBadgeClass, PAYMENT_STATUS_LABELS } from '../../utils/paymentStatus';
import { toast } from 'react-toastify';

const ExternalPropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useContext(AppContext);
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const data = await propertiesAPI.getById(id);
        if (!c) setP(data);
      } catch {
        if (!c) toast.error('Failed to load property');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => { c = true; };
  }, [id]);

  const back = () => {
    const u = p?.collectionUnit;
    if (u != null) navigate('/external/collection-units/' + encodeURIComponent(String(u)));
    else navigate('/external/collection-units');
  };

  return (
    <div className="h-full flex flex-col">
      <header className="p-6 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10 flex items-center gap-3">
        <button type="button" onClick={back} className="glass-card p-3 rounded-xl">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-black uppercase text-white truncate">Property</h2>
          <p className="text-xs text-white/60">{user ? getDisplayName(user) : '—'}</p>
        </div>
        <button type="button" onClick={() => logout && logout()} className="glass-card p-3 rounded-xl">
          <LogOut className="w-5 h-5 text-white" />
        </button>
      </header>
      <main className="flex-1 overflow-y-auto p-6 no-scrollbar space-y-4">
        {loading || !p ? (
          <p className="text-white/60">Loading...</p>
        ) : (
          <>
            <section className="glass-card p-4 space-y-2">
              <p className="text-white font-black text-lg">{p.valuationNo}</p>
              <p className="text-sm text-white/80">{p.ownerName || '—'}</p>
              <p className="text-xs text-white/50">Bill {p.billNo} · Unit {p.collectionUnit ?? '—'}</p>
              <p className="text-sm text-white">Amount due: {Number(p.totalDue).toLocaleString()}</p>
              <p className="text-sm text-white">Total paid: {Number(p.totalPaid ?? 0).toLocaleString()}</p>
              <span className={'inline-block px-2 py-1 rounded-lg text-xs font-black uppercase border ' + paymentStatusBadgeClass(p.paymentStatus)}>
                {PAYMENT_STATUS_LABELS[p.paymentStatus] || p.paymentStatus}
              </span>
            </section>
            <section className="glass-card p-4">
              <h3 className="text-white font-black mb-2 uppercase text-sm">Bill delivery</h3>
              {(p.deliveries || []).length === 0 ? (
                <p className="text-white/50 text-sm">No delivery recorded.</p>
              ) : (
                <ul className="space-y-2 text-sm text-white/90">
                  {p.deliveries.map((d) => (
                    <li key={d.id} className="border-b border-white/10 pb-2">
                      <p>{new Date(d.deliveryDate).toLocaleString()}</p>
                      <p className="text-white/60 text-xs">To: {d.recipientName} · {d.recipientPhone}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="glass-card p-4">
              <h3 className="text-white font-black mb-2 uppercase text-sm">Payments</h3>
              {(p.payments || []).length === 0 ? (
                <p className="text-white/50 text-sm">No payments recorded.</p>
              ) : (
                <ul className="space-y-2 text-sm text-white/90">
                  {p.payments.map((x) => (
                    <li key={x.id} className="border-b border-white/10 pb-2">
                      <p>{new Date(x.paymentDate).toLocaleString()} · {Number(x.amount).toLocaleString()}</p>
                      <p className="text-white/60 text-xs">{x.paymentMethod}{x.verified ? ' · verified' : ''}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default ExternalPropertyDetail;
